import React, { useEffect, useMemo, useState } from "react";
import {
  Input,
  Button,
  Modal,
  Select,
  Radio,
  Checkbox,
  DatePicker,
  TimePicker,
  Alert,
  message,
  Row,
  Col,
  Divider,
  Table,
} from "antd";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm, Controller } from "react-hook-form";
import axios from "axios";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import startCase from "lodash/startCase";
import ButtonLoader from "../ButtonLoader";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

// Mirrors PACKAGE_RULES in digital-signage-apis/api/campaigns/campaign.constants.js.
// Used only for a live client-side preview; the server always recomputes and
// stores the authoritative values on create/update.
const PACKAGE_RULES = {
  platinum: { ad_loop_minutes: 2, ad_play_per_hour: 30 },
  gold: { ad_loop_minutes: 4, ad_play_per_hour: 15 },
};

const SLOT_DURATION_OPTIONS = [
  { value: 10, label: "10 seconds" },
  { value: 15, label: "15 seconds" },
  { value: 20, label: "20 seconds" },
];

const DAYS = [
  { value: 0, label: "Sun" },
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
];

const schema = yup.object().shape({
  title: yup.string().required("Campaign title is required"),
  package: yup.string().required("Package is required"),
  start_date: yup.string().required("Start date is required"),
  end_date: yup
    .string()
    .required("End date is required")
    .test("is-after-start", "End date must be on or after start date", function (value) {
      const { start_date } = this.parent;
      return !start_date || !value || dayjs(value).isSameOrAfter(dayjs(start_date));
    }),
  start_time: yup.string().required("Start time is required"),
  end_time: yup.string().required("End time is required"),
  campaign_days: yup.array().min(1, "Select at least one day").required(),
  slot_duration_seconds: yup
    .number()
    .typeError("Slot duration is required")
    .oneOf(SLOT_DURATION_OPTIONS.map((opt) => opt.value), "Select a valid slot duration")
    .required("Slot duration is required"),
  status: yup.string().required("Status is required"),
  city_ids: yup.array().min(1, "Select at least one city").required(),
  location_ids: yup.array().min(1, "Select at least one location").required(),
  campaign_playlist_landscape_id: yup
    .number()
    .nullable()
    .transform((v, o) => (o === "" ? null : v))
    .test(
      "landscape-or-portrait-required",
      "Either campaign playlist (landscape) or (portrait) is required",
      function (value) {
        return !!value || !!this.parent.campaign_playlist_portrait_id;
      }
    ),
  campaign_playlist_portrait_id: yup
    .number()
    .nullable()
    .transform((v, o) => (o === "" ? null : v))
    .test(
      "landscape-or-portrait-required",
      "Either campaign playlist (landscape) or (portrait) is required",
      function (value) {
        return !!value || !!this.parent.campaign_playlist_landscape_id;
      }
    ),
  tablespace_playlist_landscape_id: yup.number().nullable().transform((v, o) => (o === "" ? null : v)),
  tablespace_playlist_portrait_id: yup.number().nullable().transform((v, o) => (o === "" ? null : v)),
});

// Mirrors validateCampaignPlaylists in digital-signage-apis/api/campaigns/campaign.service.js.
// Used only for a live client-side preview; the server always re-validates
// and is the source of truth on create/update.
const computePlaylistPreview = (values, playlists) => {
  const packageRule = PACKAGE_RULES[values.package];
  if (!packageRule) return null;

  const byId = new Map(playlists.map((p) => [p.id, p]));
  const campaignLandscape = byId.get(values.campaign_playlist_landscape_id);
  const campaignPortrait = byId.get(values.campaign_playlist_portrait_id);
  const tablespaceLandscape = byId.get(values.tablespace_playlist_landscape_id);
  const tablespacePortrait = byId.get(values.tablespace_playlist_portrait_id);

  const loopSeconds = packageRule.ad_loop_minutes * 60;
  const landscapeTotalSeconds =
    (campaignLandscape?.total_duration_seconds || 0) + (tablespaceLandscape?.total_duration_seconds || 0);
  const portraitTotalSeconds =
    (campaignPortrait?.total_duration_seconds || 0) + (tablespacePortrait?.total_duration_seconds || 0);

  return {
    loop_seconds: loopSeconds,
    landscape_total_seconds: landscapeTotalSeconds,
    portrait_total_seconds: portraitTotalSeconds,
    landscape_over_budget: landscapeTotalSeconds > loopSeconds,
    portrait_over_budget: portraitTotalSeconds > loopSeconds,
  };
};

const computePreview = (values) => {
  const packageRule = PACKAGE_RULES[values.package];
  if (!packageRule || !values.start_time || !values.end_time) return null;

  const start = dayjs(values.start_time, "HH:mm:ss");
  let end = dayjs(values.end_time, "HH:mm:ss");
  if (!end.isAfter(start)) end = end.add(1, "day");
  const sellableHoursDuration = Number((end.diff(start, "minute") / 60).toFixed(2));

  let totalCampaignDuration = 0;
  if (values.start_date && values.end_date && values.campaign_days?.length) {
    const daySet = new Set(values.campaign_days.map(Number));
    let cursor = dayjs(values.start_date);
    const endDate = dayjs(values.end_date);
    while (cursor.isSameOrBefore(endDate, "day")) {
      if (daySet.has(cursor.day())) totalCampaignDuration += 1;
      cursor = cursor.add(1, "day");
    }
  }

  const impressionsPerScreenPerDay = Math.floor(
    sellableHoursDuration * packageRule.ad_play_per_hour
  );

  return {
    ad_loop_minutes: packageRule.ad_loop_minutes,
    ad_play_per_hour: packageRule.ad_play_per_hour,
    sellable_hours_duration: sellableHoursDuration,
    total_campaign_duration: totalCampaignDuration,
    impressions_per_screen_per_day: impressionsPerScreenPerDay,
  };
};

const CampaignForm = ({ isModalOpen, editingCampaign, closeModal, handleUpdateList }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [errorsMessage, setErrorsMessage] = useState("");

  const [cities, setCities] = useState([]);
  const [locations, setLocations] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [locationStats, setLocationStats] = useState([]);
  const [locationStatsLoading, setLocationStatsLoading] = useState(false);
  const [overlapWarnings, setOverlapWarnings] = useState([]);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      status: "active",
      package: "platinum",
      campaign_days: [],
      city_ids: [],
      location_ids: [],
      campaign_playlist_landscape_id: null,
      campaign_playlist_portrait_id: null,
      tablespace_playlist_landscape_id: null,
      tablespace_playlist_portrait_id: null,
    },
  });

  const watchedValues = watch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const preview = useMemo(() => computePreview(watchedValues), [
    watchedValues.package,
    watchedValues.start_time,
    watchedValues.end_time,
    watchedValues.start_date,
    watchedValues.end_date,
    watchedValues.campaign_days,
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const playlistPreview = useMemo(() => computePlaylistPreview(watchedValues, playlists), [
    watchedValues.package,
    watchedValues.campaign_playlist_landscape_id,
    watchedValues.campaign_playlist_portrait_id,
    watchedValues.tablespace_playlist_landscape_id,
    watchedValues.tablespace_playlist_portrait_id,
    playlists,
  ]);

  // Warns when another active campaign already targets one of the selected
  // locations with an overlapping date/day/time window - the two campaigns
  // will rotate turns on-screen rather than merging, but the admin should
  // still see how much combined ad-loop time the other one is already
  // using there. Debounced since this fires on every relevant field edit.
  useEffect(() => {
    const locationIds = watchedValues.location_ids || [];
    const { start_date, end_date, start_time, end_time, campaign_days } = watchedValues;
    if (!locationIds.length || !start_date || !end_date || !start_time || !end_time || !campaign_days?.length) {
      setOverlapWarnings([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/api/campaigns/check-location-overlap`,
          {
            id: editingCampaign?.id || null,
            location_ids: locationIds,
            start_date: dayjs(start_date).format("YYYY-MM-DD"),
            end_date: dayjs(end_date).format("YYYY-MM-DD"),
            start_time,
            end_time,
            campaign_days,
          }
        );
        setOverlapWarnings(response.data.data || []);
      } catch (err) {
        // Non-blocking - this is an informational warning, not a hard gate.
        setOverlapWarnings([]);
      }
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watchedValues.location_ids,
    watchedValues.start_date,
    watchedValues.end_date,
    watchedValues.start_time,
    watchedValues.end_time,
    watchedValues.campaign_days,
    editingCampaign,
  ]);

  // One row per distinct conflicting campaign (not per location it happens
  // to also target) - only the fact that it overlaps matters here, not
  // which specific floors it shares with this campaign.
  const conflictingCampaigns = useMemo(() => {
    const byId = new Map();
    overlapWarnings.forEach((conflict) => {
      if (!byId.has(conflict.campaign_id)) byId.set(conflict.campaign_id, conflict);
    });
    return [...byId.values()];
  }, [overlapWarnings]);

  // Package budget rule: campaigns sharing the same time slot rotate turns
  // rather than merge, but a Platinum package guarantees a tight 2-minute
  // loop - if any overlapping campaign (including this one) is Platinum,
  // the combined seconds of everyone sharing that slot must still fit
  // within 2 minutes; only when every overlapping campaign is Gold does
  // the more generous 4-minute ceiling apply. Non-time-overlapping
  // conflicts don't compete for the same screen time, so they're excluded.
  const budgetCheck = useMemo(() => {
    const timeOverlapping = conflictingCampaigns.filter((c) => c.time_overlaps);
    if (timeOverlapping.length === 0 || !playlistPreview) return null;

    const packages = [watchedValues.package, ...timeOverlapping.map((c) => c.package)];
    const allowedSeconds = packages.includes("platinum") ? 120 : 240;

    const totalLandscapeSeconds =
      (playlistPreview.landscape_total_seconds || 0) +
      timeOverlapping.reduce((sum, c) => sum + (c.landscape_total_seconds || 0), 0);
    const totalPortraitSeconds =
      (playlistPreview.portrait_total_seconds || 0) +
      timeOverlapping.reduce((sum, c) => sum + (c.portrait_total_seconds || 0), 0);

    return {
      allowedSeconds,
      totalLandscapeSeconds,
      totalPortraitSeconds,
      exceedsLandscape: totalLandscapeSeconds > allowedSeconds,
      exceedsPortrait: totalPortraitSeconds > allowedSeconds,
    };
  }, [conflictingCampaigns, playlistPreview, watchedValues.package]);

  const locationOptions = useMemo(() => {
    const selectedCities = watchedValues.city_ids || [];
    if (selectedCities.length === 0) return [];
    return locations.filter((loc) => selectedCities.includes(loc.city_id));
  }, [locations, watchedValues.city_ids]);

  // Locations that have floor-level child locations (parent_id pointing to
  // them) represent a building, not a selectable target - only their floors
  // (leaf locations) are valid campaign targets, so the building itself is
  // disabled in the select.
  const locationIdsWithFloors = useMemo(
    () => new Set(locations.filter((loc) => loc.parent_id).map((loc) => loc.parent_id)),
    [locations]
  );

  // Orders a set of locations (all from the same city) so that each parent
  // location is immediately followed by its floor-level children, instead
  // of a flat alphabetical list.
  const sortLocationsHierarchically = (locs) => {
    const byId = new Map(locs.map((loc) => [loc.id, loc]));
    const childrenByParent = new Map();
    const roots = [];

    for (const loc of locs) {
      // Treat as a root if it has no parent, or its parent isn't in this set.
      if (loc.parent_id && byId.has(loc.parent_id)) {
        if (!childrenByParent.has(loc.parent_id)) {
          childrenByParent.set(loc.parent_id, []);
        }
        childrenByParent.get(loc.parent_id).push(loc);
      } else {
        roots.push(loc);
      }
    }

    const byName = (a, b) => a.name.localeCompare(b.name);
    roots.sort(byName);
    for (const children of childrenByParent.values()) children.sort(byName);

    const ordered = [];
    const appendWithChildren = (loc, depth) => {
      ordered.push({ loc, depth });
      const children = childrenByParent.get(loc.id) || [];
      children.forEach((child) => appendWithChildren(child, depth + 1));
    };
    roots.forEach((root) => appendWithChildren(root, 0));

    return ordered;
  };

  // Grouped by city so the Locations select shows locations nested under
  // the city they belong to (only relevant once multiple cities are picked),
  // with each parent location followed by its floor-level children.
  const groupedLocationOptions = useMemo(() => {
    const locationsByCity = new Map();
    for (const loc of locationOptions) {
      if (!locationsByCity.has(loc.city_id)) {
        locationsByCity.set(loc.city_id, { city_name: loc.city_name, locs: [] });
      }
      locationsByCity.get(loc.city_id).locs.push(loc);
    }

    return [...locationsByCity.values()]
      .sort((a, b) => a.city_name.localeCompare(b.city_name))
      .map(({ city_name, locs }) => ({
        label: city_name,
        options: sortLocationsHierarchically(locs).map(({ loc, depth }) => ({
          value: loc.id,
          label: `${" ".repeat(depth)}${loc.name}`,
          disabled: locationIdsWithFloors.has(loc.id),
        })),
      }));
  }, [locationOptions, locationIdsWithFloors]);

  const fetchMasterData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/campaigns/masters`
      );
      setCities(response.data.data.cities || []);
      setLocations(response.data.data.locations || []);
      setPlaylists(response.data.data.playlists || []);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const fetchLocationStats = async (locationIds) => {
    if (!locationIds || locationIds.length === 0) {
      setLocationStats([]);
      return;
    }
    try {
      setLocationStatsLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/campaigns/location-stats`,
        { params: { locations: locationIds.join(",") } }
      );
      setLocationStats(response.data.data || []);
      setLocationStatsLoading(false);
    } catch (err) {
      setLocationStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchMasterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const populateEditingCampaign = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/campaigns/${editingCampaign.id}`
        );
        const data = response.data.data;

        const cityIds = [...new Set((data.locations || []).map((l) => l.city_id))];
        const locationIds = data.location_ids || [];
        await fetchLocationStats(locationIds);

        reset({
          title: data.title,
          package: data.package,
          start_date: dayjs(data.start_date).format("YYYY-MM-DD"),
          end_date: dayjs(data.end_date).format("YYYY-MM-DD"),
          start_time: data.start_time,
          end_time: data.end_time,
          campaign_days: data.campaign_days || [],
          slot_duration_seconds: data.slot_duration_seconds,
          status: data.status,
          city_ids: cityIds,
          location_ids: locationIds,
          campaign_playlist_landscape_id: data.campaign_playlist_landscape_id,
          campaign_playlist_portrait_id: data.campaign_playlist_portrait_id,
          tablespace_playlist_landscape_id: data.tablespace_playlist_landscape_id,
          tablespace_playlist_portrait_id: data.tablespace_playlist_portrait_id,
        });
        setLoading(false);
      } catch (err) {
        setLoading(false);
      }
    };

    if (editingCampaign) {
      populateEditingCampaign();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingCampaign]);

  const handleCityChange = (values) => {
    setValue("city_ids", values, { shouldValidate: true });
    setValue("location_ids", [], { shouldValidate: true });
    setLocationStats([]);
  };

  const handleLocationChange = (values) => {
    setValue("location_ids", values, { shouldValidate: true });
    fetchLocationStats(values);
  };

  // Campaign playlists must fit within a single ad slot, so ones that don't
  // are shown but disabled rather than hidden - Tablespace playlists have no
  // such limit, since they fill out the rest of the loop.
  //
  // Leads with an explicit "no playlist" option rather than relying only on
  // the Select's built-in allowClear ("x") button - that button only shows
  // on hover, which doesn't work on touchscreens (this console is often
  // used on tablets/kiosks), leaving no way to clear a selection there.
  // antd warns (and misbehaves) if an option's value is null, so the "no
  // playlist" sentinel uses "" instead - onPlaylistSelectChange below maps
  // it back to null before it reaches react-hook-form.
  const buildPlaylistOptions = (orientation, { enforceSlot = false } = {}) => [
    { value: "", label: "— No playlist —" },
    ...playlists
      .filter((p) => p.orientation === orientation)
      .map((p) => {
        const tooLongForSlot =
          enforceSlot && (p.total_duration_seconds || 0) > (watchedValues.slot_duration_seconds || 0);
        return {
          value: p.id,
          disabled: tooLongForSlot,
          label: `${p.title} (${p.total_duration_seconds || 0}s)${tooLongForSlot ? " - exceeds slot duration" : ""}`,
        };
      }),
  ];

  // Pairs with buildPlaylistOptions' "" sentinel - translates it back to
  // null for react-hook-form/the backend, which both expect null (not "")
  // to mean "no playlist".
  const onPlaylistSelectChange = (field) => (value) => field.onChange(value === "" ? null : value);

  const onSubmit = async (data) => {
    const payload = {
      title: data.title,
      package: data.package,
      start_date: data.start_date,
      end_date: data.end_date,
      start_time: data.start_time,
      end_time: data.end_time,
      campaign_days: data.campaign_days,
      slot_duration_seconds: data.slot_duration_seconds,
      status: data.status,
      location_ids: data.location_ids,
      campaign_playlist_landscape_id: data.campaign_playlist_landscape_id || null,
      campaign_playlist_portrait_id: data.campaign_playlist_portrait_id || null,
      tablespace_playlist_landscape_id: data.tablespace_playlist_landscape_id || null,
      tablespace_playlist_portrait_id: data.tablespace_playlist_portrait_id || null,
    };

    const url = editingCampaign
      ? `${process.env.REACT_APP_BASE_URL}/api/campaigns/update`
      : `${process.env.REACT_APP_BASE_URL}/api/campaigns/create`;

    try {
      setSubmitting(true);
      if (editingCampaign) {
        await axios.put(url, { ...payload, id: editingCampaign.id });
      } else {
        await axios.post(url, payload);
      }
      message.success(editingCampaign ? "Campaign updated" : "Campaign created");
      handleUpdateList();
      closeModal();
    } catch (error) {
      setErrorsMessage(error.response?.data?.message || "Error submitting form");
      setShowErrors(true);
      setTimeout(() => setShowErrors(false), 3000);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={editingCampaign ? "Edit Campaign" : "Add New Campaign"}
      open={isModalOpen}
      onCancel={() => {
        reset();
        closeModal();
      }}
      footer={null}
      maskClosable={false}
      width={800}
    >
      {loading ? (
        <ButtonLoader />
      ) : (
        <>
          {showErrors && (
            <Alert message={errorsMessage} type="error" closeIcon={false} className="mb-3" />
          )}
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-3">
              <label className="form-label">
                Campaign Title <span className="text-danger">*</span>
              </label>
              <Controller
                name="title"
                control={control}
                render={({ field }) => <Input {...field} />}
              />
              {errors.title && <div className="text-danger small">{errors.title.message}</div>}
            </div>

            <div className="mb-3">
              <label className="form-label">
                Package <span className="text-danger">*</span>
              </label>
              <br />
              <Controller
                name="package"
                control={control}
                render={({ field }) => (
                  <Radio.Group {...field}>
                    <Radio value="platinum">Platinum (2 min loop, 30 plays/hr)</Radio>
                    <Radio value="gold">Gold (4 min loop, 15 plays/hr)</Radio>
                  </Radio.Group>
                )}
              />
              {errors.package && <div className="text-danger small">{errors.package.message}</div>}
            </div>

            <Row gutter={16}>
              <Col span={12}>
                <div className="mb-3">
                  <label className="form-label">
                    Campaign Start Date <span className="text-danger">*</span>
                  </label>
                  <Controller
                    name="start_date"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        style={{ width: "100%" }}
                        format="DD-MM-YYYY"
                        value={field.value ? dayjs(field.value) : null}
                        onChange={(date) =>
                          field.onChange(date ? date.format("YYYY-MM-DD") : null)
                        }
                        disabledDate={
                          editingCampaign ? undefined : (date) => date.isBefore(dayjs(), "day")
                        }
                      />
                    )}
                  />
                  {errors.start_date && (
                    <div className="text-danger small">{errors.start_date.message}</div>
                  )}
                </div>
              </Col>
              <Col span={12}>
                <div className="mb-3">
                  <label className="form-label">
                    Campaign End Date <span className="text-danger">*</span>
                  </label>
                  <Controller
                    name="end_date"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        style={{ width: "100%" }}
                        format="DD-MM-YYYY"
                        value={field.value ? dayjs(field.value) : null}
                        onChange={(date) =>
                          field.onChange(date ? date.format("YYYY-MM-DD") : null)
                        }
                        disabledDate={
                          editingCampaign
                            ? undefined
                            : (date) =>
                                date.isBefore(dayjs(), "day") ||
                                (watchedValues.start_date && date.isBefore(dayjs(watchedValues.start_date), "day"))
                        }
                      />
                    )}
                  />
                  {errors.end_date && (
                    <div className="text-danger small">{errors.end_date.message}</div>
                  )}
                </div>
              </Col>
            </Row>

            <div className="mb-3">
              <label className="form-label">
                Campaign Days <span className="text-danger">*</span>
              </label>
              <br />
              <Controller
                name="campaign_days"
                control={control}
                render={({ field }) => (
                  <>
                    <Checkbox
                      checked={field.value?.length === DAYS.length}
                      indeterminate={
                        field.value?.length > 0 && field.value.length < DAYS.length
                      }
                      onChange={(e) =>
                        field.onChange(e.target.checked ? DAYS.map((d) => d.value) : [])
                      }
                      style={{ marginBottom: 8 }}
                    >
                      Select All
                    </Checkbox>
                    <br />
                    <Checkbox.Group {...field} options={DAYS} />
                  </>
                )}
              />
              {errors.campaign_days && (
                <div className="text-danger small">{errors.campaign_days.message}</div>
              )}
            </div>

            <Row gutter={16}>
              <Col span={12}>
                <div className="mb-3">
                  <label className="form-label">
                    Campaign Start Time <span className="text-danger">*</span>
                  </label>
                  <Controller
                    name="start_time"
                    control={control}
                    render={({ field }) => (
                      <TimePicker
                        style={{ width: "100%" }}
                        format="HH:mm"
                        value={field.value ? dayjs(field.value, "HH:mm:ss") : null}
                        onChange={(time) =>
                          field.onChange(time ? time.format("HH:mm:ss") : null)
                        }
                      />
                    )}
                  />
                  {errors.start_time && (
                    <div className="text-danger small">{errors.start_time.message}</div>
                  )}
                </div>
              </Col>
              <Col span={12}>
                <div className="mb-3">
                  <label className="form-label">
                    Campaign End Time <span className="text-danger">*</span>
                  </label>
                  <Controller
                    name="end_time"
                    control={control}
                    render={({ field }) => (
                      <TimePicker
                        style={{ width: "100%" }}
                        format="HH:mm"
                        value={field.value ? dayjs(field.value, "HH:mm:ss") : null}
                        onChange={(time) =>
                          field.onChange(time ? time.format("HH:mm:ss") : null)
                        }
                      />
                    )}
                  />
                  {errors.end_time && (
                    <div className="text-danger small">{errors.end_time.message}</div>
                  )}
                </div>
              </Col>
            </Row>

            <div className="mb-3">
              <label className="form-label">
                Slot Duration of Campaign Media <span className="text-danger">*</span>
              </label>
              <Controller
                name="slot_duration_seconds"
                control={control}
                render={({ field }) => (
                  <Select {...field} style={{ width: "100%" }} options={SLOT_DURATION_OPTIONS} />
                )}
              />
              {errors.slot_duration_seconds && (
                <div className="text-danger small">{errors.slot_duration_seconds.message}</div>
              )}
            </div>

            {preview && (
              <Alert
                type="info"
                showIcon
                className="mb-3"
                message="Computed Values (preview - recalculated on save)"
                description={
                  <>
                    Sellable Hours Duration: <b>{preview.sellable_hours_duration}</b> hrs/day
                    &nbsp;|&nbsp; Total Campaign Duration: <b>{preview.total_campaign_duration}</b> days
                    &nbsp;|&nbsp; Impressions/Screen/Day: <b>{preview.impressions_per_screen_per_day}</b>
                  </>
                }
              />
            )}

            <Divider orientation="left">Targeting</Divider>

            <div className="mb-3">
              <label className="form-label">
                Cities <span className="text-danger">*</span>
              </label>
              <Controller
                name="city_ids"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    mode="multiple"
                    showSearch
                    style={{ width: "100%" }}
                    optionFilterProp="label"
                    onChange={handleCityChange}
                    options={cities.map((c) => ({ value: c.id, label: c.name }))}
                  />
                )}
              />
              {errors.city_ids && <div className="text-danger small">{errors.city_ids.message}</div>}
            </div>

            <div className="mb-3">
              <label className="form-label">
                Locations <span className="text-danger">*</span>
              </label>
              <Controller
                name="location_ids"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    mode="multiple"
                    showSearch
                    style={{ width: "100%" }}
                    optionFilterProp="label"
                    disabled={locationOptions.length === 0}
                    placeholder={locationOptions.length === 0 ? "Select a city first" : undefined}
                    onChange={handleLocationChange}
                    options={groupedLocationOptions}
                  />
                )}
              />
              {errors.location_ids && (
                <div className="text-danger small">{errors.location_ids.message}</div>
              )}
            </div>

            {watchedValues.location_ids?.length > 0 && (
              <div className="mb-3">
                <Table
                  size="small"
                  loading={locationStatsLoading}
                  pagination={false}
                  rowKey="location_id"
                  dataSource={watchedValues.location_ids.map((id) => {
                    const stat = locationStats.find((s) => s.location_id === id) || {};
                    const loc = locations.find((l) => l.id === id);
                    return {
                      location_id: id,
                      floor_name: loc?.name || id,
                      audience_count: stat.audience_count,
                      screen_count: stat.screen_count,
                    };
                  })}
                  columns={[
                    { title: "Floor", dataIndex: "floor_name" },
                    {
                      title: "Audience Count",
                      dataIndex: "audience_count",
                      render: (value) => (value ? value : ""),
                    },
                    { title: "Screens", dataIndex: "screen_count" },
                  ]}
                />
              </div>
            )}

            {conflictingCampaigns.length > 0 && (
              <div className="mb-3">
                {conflictingCampaigns.map((conflict) => (
                  <Alert
                    key={conflict.campaign_id}
                    type={conflict.time_overlaps ? "warning" : "info"}
                    showIcon
                    className="mb-2"
                    message={`Overlaps "${conflict.campaign_title}" (${startCase(conflict.package)})`}
                    description={
                      conflict.time_overlaps
                        ? `Same day and time window as this campaign on at least one shared location - they'll rotate turns on screen instead of playing together. ` +
                          `"${conflict.campaign_title}" already uses ${conflict.landscape_total_seconds}s (landscape) / ` +
                          `${conflict.portrait_total_seconds}s (portrait) of combined ad-loop time.`
                        : `Same location and date range, but a different daily time window (${conflict.start_time?.slice(0, 5)}-${conflict.end_time?.slice(0, 5)}), so it won't compete for the same screen time.`
                    }
                  />
                ))}
                {budgetCheck && (budgetCheck.exceedsLandscape || budgetCheck.exceedsPortrait) && (
                  <Alert
                    type="error"
                    showIcon
                    className="mb-2"
                    message="Combined ad-loop time exceeds the allowed limit"
                    description={
                      `Campaigns sharing this time slot allow at most ${budgetCheck.allowedSeconds}s combined ` +
                      `(2 min if any is Platinum, 4 min only if all are Gold). ` +
                      [
                        budgetCheck.exceedsLandscape
                          ? `Landscape totals ${budgetCheck.totalLandscapeSeconds}s.`
                          : null,
                        budgetCheck.exceedsPortrait ? `Portrait totals ${budgetCheck.totalPortraitSeconds}s.` : null,
                      ]
                        .filter(Boolean)
                        .join(" ")
                    }
                  />
                )}
              </div>
            )}

            <Divider orientation="left">Campaign Playlists</Divider>

            <Row gutter={16}>
              <Col span={12}>
                <div className="mb-3">
                  <label className="form-label">Campaign Playlist (Landscape) <span className="text-danger">*</span></label>
                  <Controller
                    name="campaign_playlist_landscape_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        value={field.value ?? ""}
                        onChange={onPlaylistSelectChange(field)}
                        allowClear
                        showSearch
                        style={{ width: "100%" }}
                        optionFilterProp="label"
                        placeholder="Select a landscape playlist"
                        options={buildPlaylistOptions("landscape", { enforceSlot: true })}
                      />
                    )}
                  />
                  {errors.campaign_playlist_landscape_id && (
                    <div className="text-danger small">{errors.campaign_playlist_landscape_id.message}</div>
                  )}
                </div>
              </Col>
              <Col span={12}>
                <div className="mb-3">
                  <label className="form-label">Campaign Playlist (Portrait) <span className="text-danger">*</span></label>
                  <Controller
                    name="campaign_playlist_portrait_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        value={field.value ?? ""}
                        onChange={onPlaylistSelectChange(field)}
                        allowClear
                        showSearch
                        style={{ width: "100%" }}
                        optionFilterProp="label"
                        placeholder="Select a portrait playlist"
                        options={buildPlaylistOptions("portrait", { enforceSlot: true })}
                      />
                    )}
                  />
                  {errors.campaign_playlist_portrait_id && (
                    <div className="text-danger small">{errors.campaign_playlist_portrait_id.message}</div>
                  )}
                </div>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <div className="mb-3">
                  <label className="form-label">Tablespace Playlist (Landscape) <span className="text-muted">(Optional)</span></label>
                  <Controller
                    name="tablespace_playlist_landscape_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        value={field.value ?? ""}
                        onChange={onPlaylistSelectChange(field)}
                        allowClear
                        showSearch
                        style={{ width: "100%" }}
                        optionFilterProp="label"
                        placeholder="Select a landscape playlist (optional)"
                        options={buildPlaylistOptions("landscape")}
                      />
                    )}
                  />
                </div>
              </Col>
              <Col span={12}>
                <div className="mb-3">
                  <label className="form-label">Tablespace Playlist (Portrait) <span className="text-muted">(Optional)</span></label>
                  <Controller
                    name="tablespace_playlist_portrait_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        value={field.value ?? ""}
                        onChange={onPlaylistSelectChange(field)}
                        allowClear
                        showSearch
                        style={{ width: "100%" }}
                        optionFilterProp="label"
                        placeholder="Select a portrait playlist (optional)"
                        options={buildPlaylistOptions("portrait")}
                      />
                    )}
                  />
                </div>
              </Col>
            </Row>

            {playlistPreview && (
              <Alert
                type={
                  playlistPreview.landscape_over_budget || playlistPreview.portrait_over_budget
                    ? "error"
                    : "info"
                }
                showIcon
                className="mb-3"
                message="Playlist Duration (preview - revalidated on save)"
                description={
                  <>
                    Landscape Total:{" "}
                    <b>
                      {playlistPreview.landscape_total_seconds}s / {playlistPreview.loop_seconds}s
                    </b>{" "}
                    ad loop
                    {playlistPreview.landscape_over_budget && " - exceeds loop duration"}
                    <br />
                    Portrait Total:{" "}
                    <b>
                      {playlistPreview.portrait_total_seconds}s / {playlistPreview.loop_seconds}s
                    </b>{" "}
                    ad loop
                    {playlistPreview.portrait_over_budget && " - exceeds loop duration"}
                  </>
                }
              />
            )}

            <div className="mb-3">
              <label className="form-label">
                Status <span className="text-danger">*</span>
              </label>
              <br />
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Radio.Group {...field}>
                    <Radio value="active">Active</Radio>
                    <Radio value="inactive">Inactive</Radio>
                  </Radio.Group>
                )}
              />
            </div>

            <Button
              type="primary"
              htmlType="submit"
              block
              disabled={submitting || (budgetCheck && (budgetCheck.exceedsLandscape || budgetCheck.exceedsPortrait))}
            >
              {submitting ? (
                <ButtonLoader text="Submitting..." />
              ) : editingCampaign ? (
                "Update Campaign"
              ) : (
                "Add Campaign"
              )}
            </Button>
          </form>
        </>
      )}
    </Modal>
  );
};

export default CampaignForm;
